import { Graphics, LINE_CAP } from "pixi.js"

// The shared chrome of the HUD's round gauges — the pre-wave COUNTDOWN DIAL (countdown_timer.ts)
// and the WAVE BADGE that takes its slot once the wave lands (wave_indicator.ts).
//
// The two are meant to read as one object handing over: the countdown drains to zero, vanishes, and
// the badge grows into the same spot already full. That only works if the face, groove and rim are
// literally identical, which is what this module guarantees. What separates them is the GAUGE —
// palette, direction, what it measures — and that stays in each widget.
//
// Everything here is drawn with plain fills and strokes. No filters: a soft edge is a fatter, fainter
// copy of the same path, because the low-end hardware this game targets cannot spare a filter pass.

export const DIAL_FACE_COLOR = 0x1b1512 // the ember UI's panel brown (command wheel, role picker, speech bubble)
export const DIAL_FACE_ALPHA = 0.72
export const DIAL_GROOVE_COLOR = 0x000000
export const DIAL_GROOVE_ALPHA = 0.32
export const DIAL_RIM_COLOR = 0xffffff
export const DIAL_RIM_ALPHA = 0.1

export interface DialMetrics {
    radius: number
    strokeWidth: number
}

// Radius the gauge arc is stroked along: half a stroke in from the dial's edge, so the stroke sits
// wholly inside the face rather than straddling it.
export const dialArcRadius = ({ radius, strokeWidth }: DialMetrics): number => radius - strokeWidth / 2

// Radius of the dark face behind the gauge. Half a stroke PROUD of the arc's outer edge, which is
// what gives the gauge a rim to run inside.
export const dialFaceRadius = ({ radius, strokeWidth }: DialMetrics): number => radius + strokeWidth / 2

// Outer painted size of a dial — what a caller positions against. Deliberately computed rather than
// measured: `width` on a live dial moves with its glow, its gauge and its entrance scale, so laying
// out against it makes the HUD's geometry depend on the frame it was read on.
export const dialSize = (metrics: DialMetrics): number => metrics.radius * 2 + metrics.strokeWidth

export interface DialFaceProps extends DialMetrics {
    faceColor?: number
    faceAlpha?: number
    // Twelve faint hour marks just inside the groove. On for a dial that measures TIME, off for one
    // that counts things — an enemy tally has no hours.
    hourMarks?: boolean
}

/**
 * Draw a dial's static chrome into `g`, which is cleared first.
 *
 * Drawn ONCE per widget (the gauge on top of it is what moves), so it can afford the three passes
 * that give it depth: a translucent face for legibility over a bright, busy camp, the empty groove
 * the gauge runs in, and a thin bright rim for a crisp edge.
 */
export const drawDialFace = (g: Graphics, props: DialFaceProps) => {
    const arcRadius = dialArcRadius(props)
    const faceRadius = dialFaceRadius(props)

    g.clear()

    g.beginFill(props.faceColor ?? DIAL_FACE_COLOR, props.faceAlpha ?? DIAL_FACE_ALPHA)
    g.drawCircle(0, 0, faceRadius)
    g.endFill()

    g.lineStyle({ width: props.strokeWidth, color: DIAL_GROOVE_COLOR, alpha: DIAL_GROOVE_ALPHA, cap: LINE_CAP.ROUND })
    g.drawCircle(0, 0, arcRadius)

    if (props.hourMarks) {
        // Inside the groove, never under the numerals: the longest mark stops well short of the
        // radius the big digits reach.
        const outer = arcRadius - props.strokeWidth / 2 - 2
        for (let hour = 0; hour < 12; hour++) {
            const quarter = hour % 3 === 0
            const angle = -Math.PI / 2 + (hour / 12) * Math.PI * 2
            const inner = outer - (quarter ? 5 : 3)
            g.lineStyle({ width: quarter ? 1.5 : 1, color: 0xffffff, alpha: quarter ? 0.2 : 0.11 })
            g.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner)
            g.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer)
        }
    }

    g.lineStyle({ width: 1.5, color: DIAL_RIM_COLOR, alpha: DIAL_RIM_ALPHA })
    g.drawCircle(0, 0, faceRadius - 1)
}

export interface DialGaugeProps extends DialMetrics {
    // 0..1 of the ring to fill, swept CLOCKWISE from twelve o'clock.
    progress: number
    color: number
    // Width added to the fatter, fainter copy drawn behind the arc, and its alpha. This is the
    // "glow": a second stroke, not a filter.
    haloWidth?: number
    haloAlpha?: number
    // Colour of the arc itself, when it should differ from the halo's (a flash washing it white).
    arcColor?: number
    // Bright bead at the draining end, so the eye can find how far round the gauge has got.
    bead?: boolean
}

/** Draw a gauge arc into `g`, which is cleared first. A non-positive progress leaves it empty. */
export const drawDialGauge = (g: Graphics, props: DialGaugeProps) => {
    g.clear()
    if (!(props.progress > 0)) {
        return
    }

    const arcRadius = dialArcRadius(props)
    const startAngle = -Math.PI / 2
    const endAngle = startAngle + Math.min(1, props.progress) * Math.PI * 2
    const startX = Math.cos(startAngle) * arcRadius
    const startY = Math.sin(startAngle) * arcRadius

    if (props.haloWidth && props.haloAlpha) {
        g.lineStyle({
            width: props.strokeWidth + props.haloWidth,
            color: props.color,
            alpha: props.haloAlpha,
            cap: LINE_CAP.ROUND,
        })
        g.moveTo(startX, startY)
        g.arc(0, 0, arcRadius, startAngle, endAngle)
    }

    g.lineStyle({
        width: props.strokeWidth,
        color: props.arcColor ?? props.color,
        alpha: 1,
        cap: LINE_CAP.ROUND,
    })
    g.moveTo(startX, startY)
    g.arc(0, 0, arcRadius, startAngle, endAngle)

    if (props.bead !== false) {
        g.lineStyle(0)
        g.beginFill(0xffffff, 0.85)
        g.drawCircle(Math.cos(endAngle) * arcRadius, Math.sin(endAngle) * arcRadius, props.strokeWidth * 0.3)
        g.endFill()
    }
}

/** Mix two packed RGB colours. `factor` is clamped, so callers can hand it raw easing output. */
export const interpolateColor = (from: number, to: number, factor: number): number => {
    const f = Math.max(0, Math.min(1, factor))
    const r = Math.round(((from >> 16) & 0xff) + (((to >> 16) & 0xff) - ((from >> 16) & 0xff)) * f)
    const g = Math.round(((from >> 8) & 0xff) + (((to >> 8) & 0xff) - ((from >> 8) & 0xff)) * f)
    const b = Math.round((from & 0xff) + ((to & 0xff) - (from & 0xff)) * f)
    return (r << 16) | (g << 8) | b
}

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - Math.max(0, Math.min(1, t)), 3)

// ===== the pill under a dial ==========================================================

// Both dials caption themselves with the same pill in the same place, because both are captioned
// with the same thing: "WAVE 3" on the countdown is the wave that is COMING, and "WAVE 3" on the
// badge is that same wave being fought. Moving or restyling it between the two would break the
// illusion that one object changed state.
export const DIAL_CHIP_FILL = 0x2a1d16
export const DIAL_CHIP_BORDER = 0xff8a3d
export const DIAL_CHIP_TEXT_COLOR = 0xffd9a8
export const DIAL_CHIP_PAD_X = 9
export const DIAL_CHIP_PAD_Y = 4
export const DIAL_CHIP_GAP = 11 // between the dial's outer edge and the top of the chip

/** The y the chip's top edge sits at, measured from the dial's CENTRE. */
export const dialChipTop = (metrics: DialMetrics): number => dialFaceRadius(metrics) + DIAL_CHIP_GAP

/**
 * Draw the pill into `g` (cleared first) around a label of the given measured size, and return the
 * y its text should be centred on.
 */
export const drawDialChip = (g: Graphics, metrics: DialMetrics, textWidth: number, textHeight: number): number => {
    const w = textWidth + DIAL_CHIP_PAD_X * 2
    const h = textHeight + DIAL_CHIP_PAD_Y * 2
    const top = dialChipTop(metrics)

    g.clear()
    g.beginFill(DIAL_CHIP_FILL, 0.92)
    g.lineStyle({ width: 1, color: DIAL_CHIP_BORDER, alpha: 0.55 })
    g.drawRoundedRect(-w / 2, top, w, h, h / 2)
    g.endFill()

    return top + h / 2
}

/**
 * How far below a dial's CENTRE the whole badge paints, chip included. Lets a caller keep the thing
 * on screen, and stack something under it, without reaching into either widget's layout.
 */
export const dialBottomExtent = (metrics: DialMetrics, chipTextHeight: number): number =>
    dialChipTop(metrics) + chipTextHeight + DIAL_CHIP_PAD_Y * 2
